import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Leaf, ArrowLeft } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "../lib/api";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [resetLink, setResetLink] = useState("");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setDone(true);
      if (data.reset_token) {
        setResetLink(`/reset-password?token=${data.reset_token}`);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7] px-6">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        <Link to="/login" className="flex items-center gap-2 text-[#6D7A6A] hover:text-[#2C362B] font-semibold mb-8 transition-colors" data-testid="back-to-login">
          <ArrowLeft size={18} weight="bold" /> Back to sign in
        </Link>
        <div className="w-10 h-10 rounded-full bg-[#8A9A86] flex items-center justify-center mb-6">
          <Leaf size={22} weight="fill" color="#fff" />
        </div>
        <h1 className="font-heading text-4xl font-semibold text-[#2C362B] mb-2">Reset your password</h1>
        <p className="text-[#6D7A6A] mb-8 leading-relaxed">
          Enter your email and we'll create a secure link to set a new password.
        </p>

        {done ? (
          <div className="bg-white rounded-[2rem] p-7 border border-[#E6EBE4]/60" data-testid="forgot-done">
            <p className="text-[#515A4F] leading-relaxed">
              If an account exists for <span className="font-semibold">{email}</span>, a reset link has been created.
            </p>
            {resetLink && (
              <Link to={resetLink} className="mt-5 inline-block w-full text-center bg-[#8A9A86] text-white hover:bg-[#6D7A6A] rounded-full px-6 py-3.5 font-semibold transition-all hover:-translate-y-0.5" data-testid="continue-reset-link">
                Continue to reset password
              </Link>
            )}
            <p className="text-xs text-[#A8B0A3] mt-4">
              No email service is connected yet, so the secure link is shown here for now.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full bg-[#F4F1E8] border-none rounded-2xl px-5 py-4 text-[#2C362B] placeholder:text-[#A8B0A3] focus:ring-2 focus:ring-[#8A9A86] focus:bg-white outline-none transition-all"
              data-testid="forgot-email-input"
            />
            {error && <p className="text-sm text-[#D97B6A] bg-[#FBEAE7] rounded-xl px-4 py-3" data-testid="forgot-error">{error}</p>}
            <button type="submit" disabled={loading} className="w-full bg-[#8A9A86] text-white hover:bg-[#6D7A6A] rounded-full px-8 py-4 font-semibold transition-all hover:-translate-y-0.5 disabled:opacity-60" data-testid="forgot-submit-btn">
              {loading ? "Please wait…" : "Send reset link"}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
